<template>
  <div class="">
    <div v-if="!isKurogo" class="position-fixed md-md-none colored-line z-2">
      <div class="line-1"></div>
      <div class="line-2"></div>
      <div class="line-3"></div>
      <div class="line-4"></div>
    </div>
    <div
      class="flexigo"
      :class="{ 'white-style': isWhite, 'in-frame': isEmbedded, 'main-menu-collapsed': isMainMenuCollapsed }"
    >
      <side-navigation v-if="!isEmbedded" />
      <flexigo-loader :isLoading="isLoading" />
      <div v-show="!isLoading" class="main-content">
        <topbar v-if="!isEmbedded" />
        <router-view
          v-if="routeAccess === true"
          :key="$route.fullPath"
          class="content-area full-height-with-topbar"
          :class="{ 'm-0': isEmbedded }"
        />
        <div v-else-if="routeAccess === false" class="content-area full-height-with-topbar p-4" role="alert">
          {{ $t('flexigo.common.noPageAccess') }}
        </div>
      </div>
    </div>
    <simple-dialog />
    <agent-chat-drawer />
  </div>
</template>

<script>
import sideNavigation from '@/modules/Flexigo/components/navigation/side-menu.vue';
import topbar from '@/modules/Flexigo/components/navigation/topbar.vue';
import simpleDialog from '@/modules/Flexigo/components/simple-dialog/Dialog.vue';
import AgentChatDrawer from '@/modules/Flexigo/components/agent/AgentChatDrawer.vue';
import { isKurogoWebview } from '@/helpers/utility';
import flexigoLoader from '../common/components/flexigoLoader.vue';
import { resolveRedirectRoute } from '../modules/Flexigo/screens/Login/loginRedirect';
import { routeAccessOf } from '../modules/Flexigo/helpers/menuAccess';

export default {
  name: 'FlexigoShell',
  components: { sideNavigation, topbar, simpleDialog, flexigoLoader, AgentChatDrawer },

  data() {
    return {
      currentComponent: 'router-view',
      isKurogo: isKurogoWebview(),
      // set once the first load below ends; store.state.isLoading is also toggled by screens
      isShellLoaded: false,
    };
  },
  computed: {
    isWhite() {
      return this.$store.state.isBackgroundWhite;
    },
    isMainMenuCollapsed() {
      return this.$store.state.isMainMenuCollapsed;
    },
    isInFrame() {
      const registerMenuItems = [
        'SelectMainCampus',
        'ApproveKvkk',
        'HomeAddress',
        'selectCompany',
        'selectDepartment',
        'workgroupShiftTime',
        'PersonalInfo',
      ];
      if (registerMenuItems.includes(this.$route.name)) return true;
      return window.top !== window.self;
    },
    isEmbedded() {
      return this.isInFrame || this.isKurogo;
    },
    isLoading() {
      return this.$store.state.isLoading || this.$store.state.isNavigating;
    },
    // a menu-gated route (flexiCar) waits for the menu and opens only when the menu has it
    routeAccess() {
      return routeAccessOf(this.$route, this.$store.state.mainMenu, this.isShellLoaded);
    },
  },
  async created() {
    console.log('Flexigo shell initiated');
    console.log(process.env.BASE_URL);

    // Restore the language requested via /auto-login: the SAML round trip is a full
    // page reload and the backend redirect URL carries no lang param
    try {
      const autoLoginContext = JSON.parse(localStorage.getItem('flexigoAutoLoginContext'));
      if (autoLoginContext?.lang && this.$i18n.locale !== autoLoginContext.lang) {
        this.$i18n.locale = autoLoginContext.lang;
      }
    } catch (error) {
      // ignore malformed context
    }

    try {
      this.$store.commit('SET_IS_LOADING', true);

      await this.$store.dispatch('SET_USER_DETAILS');

      // Company settings (length unit, presentation type, module flags, companyId)
      // are needed for every account type. Skipping FLEXIGO_VEKTOR_ADMIN left
      // `companySettings` null, so `isImperial()` and every other consumer fell
      // back to the browser locale instead of the company's setting
      // (VSER-14481 #6d). A super admin with no company in session gets a 403
      // here, so the call is isolated from the rest of the bootstrap.
      try {
        await this.$store.dispatch('SET_COMPANY_SETTINGS');
      } catch (error) {
        console.warn('Company settings unavailable for this session', error?.response?.status || error);
      }

      await this.$store.dispatch('SET_SERVER_TIMEZONE');

      await Promise.allSettled([
        this.$store.dispatch('SET_CAMPUSES'),
        this.$store.dispatch('SET_MAIN_MENU', {
          lang: this.$i18n.locale,
          isRider: this.$store.getters.userDetails.isRider,
        }),
      ]);
    } catch (error) {
      console.error(error);
    } finally {
      this.$store.commit('SET_IS_LOADING', false);
      this.isShellLoaded = true;
    }

    // Handle post-login redirect after loading completes
    // Only redirect if we're at the root/login page, not when user has a specific route in URL
    const { userDetails, companySettings } = this.$store.state;

    // Check both the URL hash and the current route name for more reliable detection
    // This handles cases where the route state might be out of sync after a restart
    const urlHash = window.location.hash;
    const currentRouteName = this.$route?.name;

    // Check if user is still on login-related pages
    const loginRoutes = ['login', 'register', 'LoginRedirector'];
    const isAtLoginOrRoot =
      !urlHash ||
      urlHash === '#/' ||
      urlHash === '#/login' ||
      urlHash.startsWith('#/login?') ||
      loginRoutes.includes(currentRouteName);

    if (isAtLoginOrRoot) {
      const targetRoute = resolveRedirectRoute(userDetails, companySettings);
      if (targetRoute) {
        console.log('Flexigo shell handling post-login redirect to:', targetRoute);
        this.$router.push(targetRoute).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        });
      }
    }

    // Honor the target stored by /auto-login before its SSO redirect; the SAML success
    // handler always lands on /rider/shared-ride. One-shot: consumed unconditionally.
    // The navigation goes through beforeEach, so onboarding redirects still win.
    const autoLoginTarget = sessionStorage.getItem('flexigoAutoLoginTarget');
    if (autoLoginTarget) {
      sessionStorage.removeItem('flexigoAutoLoginTarget');
      if (this.$route.path !== autoLoginTarget) {
        this.$router.replace(autoLoginTarget).catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err);
          }
        });
      }
    }
  },
};
</script>

<style lang="scss">
@import '../sass/flexigo/base.scss';
.white-style {
  background-color: white;
  height: 100vh;
}
</style>
